import { includes } from "lodash-es"
import { RestResource } from "../common"
import { SchemaDeSerBuilder, type SchemaDeSer } from "../deser"

export enum FuturesTradingRequestStatus {
  PENDING_JOINT_AGREEMENT = "Pending Joint Agreement",
  PENDING = "Pending",
  NEEDS_REVIEW = "Needs Review",
  APPROVED = "Approved",
  REJECTED = "Rejected",
  FAILED = "Failed",
  COMPLETE = "Complete",
}

const PENDING_STATUSES = [
  FuturesTradingRequestStatus.PENDING_JOINT_AGREEMENT,
  FuturesTradingRequestStatus.PENDING,
  FuturesTradingRequestStatus.NEEDS_REVIEW,
  FuturesTradingRequestStatus.APPROVED,
]

export class FuturesTradingRequest extends RestResource {
  static onInitialize = (_obj: FuturesTradingRequest) => {
    /* no-op */
  }

  constructor() {
    super()
    FuturesTradingRequest.onInitialize(this)
  }

  accountNumber = ""
  requestingCustomerId = 0
  createdAt?: Date
  status = ""
  approvingUserId = 0
  approvedAt?: Date
  rejectingUserId = 0
  rejectedAt?: Date
  isPendingJointAgreement = false
  isRequestingCustomer = true

  get isFailedOrRejected() {
    return this.isFailed || this.isRejected
  }

  get isFailed() {
    return this.status === FuturesTradingRequestStatus.FAILED
  }

  get isRejected() {
    return this.status === FuturesTradingRequestStatus.REJECTED
  }

  get isComplete() {
    return this.status === FuturesTradingRequestStatus.COMPLETE
  }

  get isPendingCompletion() {
    return includes(PENDING_STATUSES, this.status)
  }

  get isPendingJoint() {
    return this.status === FuturesTradingRequestStatus.PENDING_JOINT_AGREEMENT
  }

  get needsSecondaryFuturesAgreement() {
    return this.isPendingJointAgreement && !this.isRequestingCustomer
  }
}

export const FUTURES_TRADING_REQUEST_DESER: SchemaDeSer<FuturesTradingRequest> =
  new SchemaDeSerBuilder(FuturesTradingRequest)
    .ofString("id")
    .ofString("accountNumber")
    .ofString("requestingCustomerId")
    .ofDateTime("createdAt")
    .ofString("status")
    .ofString("approvingUserId")
    .ofDateTime("approvedAt")
    .ofString("rejectingUserId")
    .ofDateTime("rejectedAt")
    .ofBoolean("isPendingJointAgreement")
    .ofBoolean("isRequestingCustomer")
    .toDeSer()

export enum FuturesTradingRequestEligibilityStatus {
  ELIGIBLE = "Eligible",
  EXISTING_REQUEST = "Existing Request",
  NEEDS_OPTIONS_LEVEL_UPGRADE = "Needs Options Level Upgrade",
  ACCOUNT_CLOSED = "Account Closed",
  OWNER_REQUEST_ONLY = "Owner Request Only",
}

export class FuturesTradingRequestEligibility extends RestResource {
  status = ""
  reason = ""

  get isEligible() {
    return this.status === FuturesTradingRequestEligibilityStatus.ELIGIBLE
  }

  get hasExistingRequest() {
    return (
      this.status === FuturesTradingRequestEligibilityStatus.EXISTING_REQUEST
    )
  }

  get needsOptionsLevelUpgrade() {
    return (
      this.status ===
      FuturesTradingRequestEligibilityStatus.NEEDS_OPTIONS_LEVEL_UPGRADE
    )
  }
}

export const FUTURES_TRADING_REQUEST_ELIGIBILITY_DESER: SchemaDeSer<FuturesTradingRequestEligibility> =
  new SchemaDeSerBuilder(FuturesTradingRequestEligibility)
    .ofString("status")
    .ofString("reason")
    .toDeSer()

export class FuturesAgreementRecord {
  static onInitialize = (_obj: FuturesAgreementRecord) => {
    /* no-op */
  }

  constructor() {
    FuturesAgreementRecord.onInitialize(this)
  }

  isRegistrationExempt = false
  registrationExemption = ""
  isRegistered = false
  registrationMember = ""
  hasFuturesIndustryAffiliation = false
  futuresIndustryFirm = ""
  isSoliciting = false
  isNfaPool = false
}

export const FUTURES_AGREEMENT_RECORD_DESER: SchemaDeSer<FuturesAgreementRecord> =
  new SchemaDeSerBuilder(FuturesAgreementRecord)
    .ofBoolean("isRegistrationExempt")
    .ofString("registrationExemption")
    .ofBoolean("isRegistered")
    .ofString("registrationMember")
    .ofBoolean("hasFuturesIndustryAffiliation")
    .ofString("futuresIndustryFirm")
    .ofBoolean("isSoliciting")
    .ofBoolean("isNfaPool")
    .toDeSer()
